"use client";

import { HeroSection } from "@/components/shared/hero_section";
import { BenefitsSection } from "@/components/shared/benefits_section";
import { IconBenefitsSection } from "@/components/shared/icon_benefits_section";
import { EvidenceSection } from "@/components/shared/evidence_section";
import { AlternatingSection } from "@/components/shared/alternating_section";
import { QuoteCarousel, QuoteData } from "@/components/shared/quote_carousel";
import { ContentDownload } from "@/components/shared/content_download";
import { IntegrationsCarouselCompact } from "@/components/shared/integrations_carousel";
import LearningStagesCarousel from "@/features/marketing/components/landing/LearningStagesCarousel";
import Image from "next/image";
import { useTranslations, useLocale } from "next-intl";

const Universities = () => {
    const t = useTranslations();

    // Example quotes data for universities
    const universityQuotes: QuoteData[] = [
        {
            text: "The pilot is an exciting initiative involving over 2,000 of our students. It reflects our commitment to using safe AI tools that enhance the student experience, while providing valuable support for both our learners and their lecturers at Loughborough.",
            author: "Sam Grogan",
            title: "Pro-Vice-Chancellor of Education and Student Experience, Loughborough University",
            image: "/sam-grogan.jpg",
            logo: "/loughborough_crest.png"
        },
        {
            text: "The pilot is hugely exciting and offers us an excellent opportunity to explore how we can use education technologies to further enhance student support and the student experience at the University of Birmingham.",
            author: "Professor Deborah Longworth",
            title: "Pro-Vice-Chancellor (Education), University of Birmingham",
            image: "/deborah.png",
            logo: "/uob_crest.png"
        },
        {
            text: "St Mary's has always prioritised the experience and success of our students. Our implementation of StudyStash within Canvas provides a great way to help all online learners stay engaged with their courses and achieve academic success.",
            author: "Kevin Braim",
            title: "Chief Information Officer",
            image: "/kevin_braim.png",
            logo: "/st_marys_crest.png"
        },
        {
            text: "StudyStash has helped our teams to deliver consistent, high-quality teaching at scale. It's made a real difference in supporting students arriving from overseas.",
            author: "Professor Matthew Leeke",
            title: "Deputy Pro Vice-Chancellor (Student Experience), University of Birmingham",
            image: "/leeke-matthew-3.jpg",
            logo: "/uob_crest.png"
        },
        {
            text: "What I really like about StudyStash is that it simplifies the revision process, but still draws on all the neuroscience of learning.",
            author: "Professor Benjamin Ellis",
            title: "StudyStash | Head of Education for School of Rehabilitation Sciences, University of Birmingham",
            image: "/quote_images/benjamin_ellis.jpg",
            logo: "/uob_crest.png"
        },
        {
            text: "It is amazing to see how nursing students value StudyStash to help with revision of complex topics such as bioscience. From a lecturer's perspective, StudyStash offers another opportunity to support preparation for assessments such as exams.",
            author: "Kelvin McMillan",
            title: "Assistant Professor in Nursing University of Birmingham",
            image: "/case-studies/kelvin-mcmillan.jpg",
            logo: "/uob_crest.png"
        },
        {
            text: "We're delighted to collaborate with StudyStash - the way they embed fundamental neuroscience principles into their platform has a significant impact on learning outcomes for university students.",
            author: "Milad Ekramnia",
            title: "Chief Executive Officer at EdCortex",
            image: "/case-studies/milad.png",
            logo: "/EdCortex.png"
        }
    ];

    return (
        <main className="pt-8">
            <IntegrationsCarouselCompact />

            <div className="flex w-full flex-col items-center">
                <HeroSection 
                    desktopImage="/apple_images.png"
                    mobileImage="/apple_image_mobile.png"
                    desktopImageAlt="StudyStash Dashboard shown on a MacBook Pro, iPad and iPhone"
                    mobileImageAlt="StudyStash Dashboard shown on a MacBook Pro"
                    heading="Secure, AI-Powered Learning Within Institutional Guardrails"
                    description="Boost student engagement, retention and outcomes with high-quality, scalable teaching and learning, all within your existing Learning Management System."
                    mobileDescription="Boost student engagement, retention and outcomes with high-quality, scalable teaching and learning, all within your existing Learning Management System."
                    buttonText="Join the pilot"
                    buttonHref="/pilot-program"
                />

                {/* Learning Journey Carousel */}
                <div id="learning-carousel" className="w-full">
                    <LearningStagesCarousel />
                </div>

                <div className="relative w-full">
                    <div className="absolute top-[400px] right-0 pointer-events-none hidden lg:block">
                        <img 
                            src="/left-right-lines.svg" 
                            alt="Illustration of the StudyStash teaching-learning loop" 
                            className="w-[300px] h-auto" 
                        />
                    </div>
                    
                    <BenefitsSection 
                        heading={`How StudyStash transforms ${t("general.words.university")} education`}
                        subheading1="Reduce DFWs, boost student engagement, and ease faculty workload across your institution."
                        subheading2="StudyStash helps students stay motivated and on track while giving faculty time back - delivering measurable impact on retention, engagement, and learning outcomes."
                        cards={[
                            {
                                title: "Enhance student learning",
                                description: `StudyStash delivers ${t("general.words.personalised")}, adaptive content and support for self-directed study and in-class learning`,
                                image: "/for-universities-small.png",
                                imageAlt: "Student learning features",
                                bgColor: "bg-ss-yellow",
                                textColor: "text-black"
                            },
                            {
                                title: "Support faculty & teaching staff",
                                description: "StudyStash saves teachers time by answering questions, generating formative tests, and focusing teaching where it matters most",
                                image: "/for-lecturers-small.png",
                                imageAlt: "Faculty support features",
                                bgColor: "bg-ss-green"
                            },
                            {
                                title: "Boost outcomes & reputation",
                                description: "StudyStash helps students manage their workload, grow in confidence, and achieve better results",
                                image: "/for-students-small.png",
                                imageAlt: "Outcomes improvement features",
                                bgColor: "bg-ss-blue"
                            }
                        ]}
                        buttonText="Learn more about our impact"
                        buttonHref="/about/case-studies"
                        showButton={true}
                    />
                </div>

                <div className="py-24 w-full">
                    <EvidenceSection 
                        source={
                            <>
                                Source - UOB pilot 2025 <span className="text-[8px] sm:text-[9px] md:text-[10px]">n=2,192</span>
                            </>
                        }
                        circles={[
                            {
                                value: "59%",
                                title: `Student ${t("general.words.attainment")}`,
                                description: "Of students reported StudyStash helped improve their academic performance"
                            },
                            {
                                value: "74%",
                                title: "Student Confidence",
                                description: "Of students reported increased confidence managing study workload"
                            },
                            {
                                value: "34%",
                                title: "Student Engagement",
                                description: useLocale() === 'en-us' ? "Of students regularly used StudyStash outside of class time (avg. 3 hours 20 mins/week)" : "Of students regularly used StudyStash outside of lecture time (avg. 3 hours 20 mins/week)"
                            }
                        ]} 
                    />
                </div>

                <div className="relative w-full">
                    <div className="absolute left-0 top-[300px] w-full h-1/2 pointer-events-none z-0 hidden sm:block">
                        <img
                            src="/left-right-loops.svg"
                            alt="Illustration of the StudyStash teaching-learning loop"
                            className="w-full h-full object-contain"
                        />
                    </div>
                    
                    <div className="relative z-10">
                        <AlternatingSection 
                            sections={[
                                {
                                    subtitle: "Enhanced student success",
                                    title: "Improve learning outcomes",
                                    content: (
                                        <div>
                                            <p>Our AI-powered platform helps universities boost student engagement and academic performance through {t("general.words.personalised")} learning experiences and intelligent study tools.</p>
                                            <p>With features like adaptive learning paths and real-time progress tracking, you can identify at-risk students early and provide targeted support when it matters most.</p>
                                        </div>
                                    ),
                                    image: {
                                        src: "/stock1.jpg",
                                        alt: "Demonstration of student success features"
                                    }
                                },
                                {
                                    subtitle: "Academic integrity",
                                    title: "Maintain educational standards",
                                    content: (
                                        <div>
                                            <p>StudyStash's AI guardrails ensure that all content is grounded in your approved course materials, maintaining academic integrity while leveraging the power of AI.</p>
                                            <p>Our platform provides comprehensive oversight and analytics, giving you confidence that AI is enhancing, not compromising, educational quality.</p>
                                        </div>
                                    ),
                                    video: {
                                        src: "/how-we-help/ai-with-guardrails/academic-integrity.gif",
                                        alt: "Demonstration of academic integrity features",
                                        containerClassName: "rounded-xl px-3 pt-4 shadow-lg transition-all duration-300 hover:shadow-xl md:px-8 md:pt-8",
                                        type: "gif"
                                    }
                                }
                            ]}
                        />
                    </div>
                </div>

                <IconBenefitsSection 
                    cards={[
                        {
                            title: "Boost student engagement",
                            description: "Track real-time engagement metrics and identify at-risk students before they fall behind.",
                            icon: <Image src="/icons/hat-icon-ss.png" alt="Student engagement icon" width={48} height={48} />,
                            bgColor: "bg-ss-yellow",
                            textColor: "text-black"
                        },
                        {
                            title: "Enhance learning outcomes",
                            description: `Provide ${t("general.words.personalised")} learning pathways that adapt to each student's needs and pace.`,
                            icon: <Image src="/icons/people-icon-ss.png" alt="Learning outcomes icon" width={48} height={48} />,
                            bgColor: "bg-ss-green"
                        },
                        {
                            title: "Drive student success",
                            description: "Get actionable insights to improve course delivery and support student outcomes.",
                            icon: <Image src="/icons/graph-icon-ss.png" alt="Student success icon" width={48} height={48} />,
                            bgColor: "bg-ss-blue"
                        }
                    ]}
                />

                <div className="my-12 w-full container mx-auto px-6 lg:px-8">
                    <QuoteCarousel quotes={universityQuotes} />
                </div>

                <div className="mb-12 lg:mb-24 overflow-visible container mx-auto px-6 lg:px-8">
                    <ContentDownload
                        title="See How Universities Succeed with StudyStash"
                        description="View our case studies to learn how leading universities are transforming student learning outcomes with StudyStash."
                        buttonText="View Case Studies"
                        buttonHref="/about/case-studies"
                        showSingleButton={true}
                    />
                </div>

            </div>
        </main>
    );
};

export default Universities;
